/**
 * Auth Service - Main Export
 *
 * Provides a unified interface to all auth-related services.
 * Maintains backward compatibility with the original authService API.
 */

import { authService as authServiceInstance } from "./authService";
import { MFAService } from "./mfaService";
import { PasswordService } from "./passwordService";
import { SessionService } from "./sessionService";
import { RoleService } from "./roleService";
import { SecurityService } from "./securityService";
import { EncryptionService } from "./encryptionService";

// Export types
export * from "./types";

// Export individual services
export { AuthService, authService as authServiceInstance } from "./authService";
export { MFAService } from "./mfaService";
export { PasswordService } from "./passwordService";
export { SessionService } from "./sessionService";
export { RoleService } from "./roleService";
export { SecurityService } from "./securityService";
export { EncryptionService } from "./encryptionService";

// Create service instances
const mfaService = new MFAService();
const passwordService = new PasswordService();
const sessionService = new SessionService();
const roleService = new RoleService();
const securityService = new SecurityService();
const encryptionService = new EncryptionService();

/**
 * Unified Auth Service API
 * Provides all auth functionality through a single interface
 * Maintains backward compatibility with the original authService
 */
export const authService = {
  // Basic Authentication
  signup: authServiceInstance.signup.bind(authServiceInstance),
  signin: authServiceInstance.signin.bind(authServiceInstance),
  signout: authServiceInstance.signout.bind(authServiceInstance),
  resetPassword: authServiceInstance.resetPassword.bind(authServiceInstance),

  // MFA Operations
  enrollMFA: mfaService.enrollMFA.bind(mfaService),
  verifyMFAEnrollment: mfaService.verifyMFAEnrollment.bind(mfaService),
  createMFAChallenge: mfaService.createMFAChallenge.bind(mfaService),
  verifyMFALogin: mfaService.verifyMFALogin.bind(mfaService),
  unenrollMFA: mfaService.unenrollMFA.bind(mfaService),
  getMFAStatus: mfaService.getMFAStatus.bind(mfaService),
  generateRecoveryCodes: mfaService.generateRecoveryCodes.bind(mfaService),
  verifyRecoveryCode: mfaService.verifyRecoveryCode.bind(mfaService),
  syncMFAEnrollment: mfaService.syncMFAEnrollment.bind(mfaService),

  // Password Operations
  changePassword: passwordService.changePassword.bind(passwordService),
  validatePassword: passwordService.validatePassword.bind(passwordService),
  checkPasswordExpiry: passwordService.checkPasswordExpiry.bind(passwordService),
  forcePasswordChange: passwordService.forcePasswordChange.bind(passwordService),
  updatePasswordExpirySettings: passwordService.updatePasswordExpirySettings.bind(passwordService),

  // Session Operations
  createSession: sessionService.createSession.bind(sessionService),
  getUserSessions: sessionService.getUserSessions.bind(sessionService),
  deleteSession: sessionService.deleteSession.bind(sessionService),
  logoutAllDevices: sessionService.logoutAllDevices.bind(sessionService),
  updateSessionHeartbeat: sessionService.updateSessionHeartbeat.bind(sessionService),
  getActiveSessionCount: sessionService.getActiveSessionCount.bind(sessionService),

  // Role & Permission Operations
  getUserRoles: roleService.getUserRoles.bind(roleService),
  verifyRole: roleService.verifyRole.bind(roleService),
  assignRole: roleService.assignRole.bind(roleService),
  getUserPermissions: roleService.getUserPermissions.bind(roleService),
  checkPermission: roleService.checkPermission.bind(roleService),
  verifyAdmin: roleService.verifyAdmin.bind(roleService),

  // Security & Profile Operations
  getSecuritySettings: securityService.getSecuritySettings.bind(securityService),
  updateSecuritySettings: securityService.updateSecuritySettings.bind(securityService),
  getAuthMetadata: securityService.getAuthMetadata.bind(securityService),
  updateEmail: securityService.updateEmail.bind(securityService),
  updatePhone: securityService.updatePhone.bind(securityService),
  checkAccountLockout: securityService.checkAccountLockout.bind(securityService),
  logLoginAttempt: securityService.logLoginAttempt.bind(securityService),
  getLoginHistory: securityService.getLoginHistory.bind(securityService),
  getFailedAttempts: securityService.getFailedAttempts.bind(securityService),
  getLockedAccounts: securityService.getLockedAccounts.bind(securityService),
  getActiveDevices: securityService.getActiveDevices.bind(securityService),
  flagSuspiciousActivity: securityService.flagSuspiciousActivity.bind(securityService),
  unlockAccount: securityService.unlockAccount.bind(securityService),
  sendEmailVerification: securityService.sendEmailVerification.bind(securityService),
  confirmEmailVerification: securityService.confirmEmailVerification.bind(securityService),
  sendPhoneVerification: securityService.sendPhoneVerification.bind(securityService),
  confirmPhoneVerification: securityService.confirmPhoneVerification.bind(securityService),
  getVerificationStatus: securityService.getVerificationStatus.bind(securityService),
  refreshToken: securityService.refreshToken.bind(securityService),
  revokeToken: securityService.revokeToken.bind(securityService),
  getActiveTokens: securityService.getActiveTokens.bind(securityService),

  // Encryption Key Operations
  getUserEncryptionKey: encryptionService.getUserEncryptionKey.bind(encryptionService),
  createOrUpdateEncryptionKey:
    encryptionService.createOrUpdateEncryptionKey.bind(encryptionService),
  deleteUserEncryptionKeys: encryptionService.deleteUserEncryptionKeys.bind(encryptionService),

  // Utility Operations
  trackReferralSignup: authServiceInstance.trackReferralSignup.bind(authServiceInstance),

  // Helper method for getting auth headers (used by other services)
  getAuthHeaders: authServiceInstance.getAuthHeaders.bind(authServiceInstance),
};

export {
  getAccessToken,
  getClientSession,
  getClientUser,
  refreshClientSession,
  signOutClientLocal,
} from "./clientSession";
